import React, { useState, useEffect } from 'react';
import { Input, Button, Modal, Spin, Checkbox, Table, Select } from 'antd';
import {
  StepBackwardOutlined, CaretLeftOutlined,
  CaretRightOutlined, StepForwardOutlined,
  PlusOutlined, EditOutlined, DeleteOutlined,
  SaveOutlined, CloseOutlined, SearchOutlined,
} from '@ant-design/icons';
import { useWarehouseLocationForm } from './hooks/useWarehouseLocationForm';
import warehouseLocationApi, { F106Option } from '../../services/warehouseLocationApi';
import './P1105Form.css';

const P1105Form: React.FC = () => {
  const {
    mode, loading,
    f105_01, f105_11, f105_12, f105_13, f105_14, f105_15,
    f105_16, f105_17, f105_18, f105_19, f105_20,
    f105_91, f105_92, f105_93,
    setF10501, setF10511, setF10512, setF10513, setF10514, setF10515,
    setF10516, setF10517, setF10518, setF10520,
    details, addDetail, removeDetail, updateDetailEmployee,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save, doDelete,
    quickSearch, recordPosition, recordCount,
    employeeNames, lookupEmployeeName,
  } = useWarehouseLocationForm();

  const [searchKey, setSearchKey] = useState('');
  const [levelOptions, setLevelOptions] = useState<F106Option[]>([]);
  const isEditing = mode === 'add' || mode === 'edit';

  useEffect(() => {
    warehouseLocationApi.listLevels().then(setLevelOptions).catch(() => {});
  }, []);

  const detailColumns = [
    {
      title: '序號',
      dataIndex: 'f1051_02',
      key: 'seq',
      width: 60,
      render: (_: number, __: any, idx: number) => idx + 1,
    },
    {
      title: '員工代號',
      dataIndex: 'f1051_11',
      key: 'empCode',
      width: 160,
      render: (val: string, _: any, idx: number) => (
        <Input
          value={val}
          onChange={(e) => updateDetailEmployee(idx, e.target.value)}
          onBlur={() => { if (val) lookupEmployeeName(val); }}
          maxLength={8}
          disabled={!isEditing}
          style={{ width: 140 }}
        />
      ),
    },
    {
      title: '員工姓名',
      dataIndex: 'f1051_11',
      key: 'empName',
      width: 120,
      render: (val: string) => (
        <span>{employeeNames[val] || ''}</span>
      ),
    },
    ...(isEditing ? [{
      title: '操作',
      key: 'action',
      width: 60,
      render: (_: any, __: any, idx: number) => (
        <Button
          type="link"
          danger
          size="small"
          onClick={() => removeDetail(idx)}
        >
          刪除
        </Button>
      ),
    }] : []),
  ];

  return (
    <Spin spinning={loading}>
      <div className="p1105-form">
        <h2>庫位建檔</h2>

        {/* Quick Search */}
        <div className="p1105-search-bar">
          <Input
            placeholder="輸入庫位代號快速搜尋"
            value={searchKey}
            onChange={(e) => setSearchKey(e.target.value)}
            onPressEnter={() => quickSearch(searchKey)}
            style={{ width: 200 }}
            disabled={isEditing}
          />
          <Button
            icon={<SearchOutlined />}
            onClick={() => quickSearch(searchKey)}
            disabled={isEditing}
          >
            搜尋
          </Button>
          <span className="p1105-record-info">
            {recordPosition} / {recordCount}
          </span>
        </div>

        {/* Navigation */}
        <div className="p1105-nav-bar">
          <Button icon={<StepBackwardOutlined />} onClick={navFirst} disabled={isEditing}>首筆</Button>
          <Button icon={<CaretLeftOutlined />} onClick={navPrev} disabled={isEditing}>前筆</Button>
          <Button icon={<CaretRightOutlined />} onClick={navNext} disabled={isEditing}>次筆</Button>
          <Button icon={<StepForwardOutlined />} onClick={navLast} disabled={isEditing}>末筆</Button>
        </div>

        {/* Form Fields */}
        <div className="p1105-fields">
          <div className="p1105-field-row">
            <label>庫位代號</label>
            <Input
              value={f105_01}
              onChange={(e) => setF10501(e.target.value)}
              maxLength={10}
              disabled={mode !== 'add'}
              style={{ width: 140 }}
            />
          </div>
          <div className="p1105-field-row">
            <label>庫位名稱</label>
            <Input
              value={f105_11}
              onChange={(e) => setF10511(e.target.value)}
              disabled={!isEditing}
              style={{ width: 280 }}
            />
          </div>
          <div className="p1105-field-row">
            <label>庫位簡稱</label>
            <Input
              value={f105_12}
              onChange={(e) => setF10512(e.target.value)}
              disabled={!isEditing}
              style={{ width: 200 }}
            />
          </div>
          <div className="p1105-field-row">
            <label>地址</label>
            <Input
              value={f105_13}
              onChange={(e) => setF10513(e.target.value)}
              disabled={!isEditing}
              style={{ width: 360 }}
            />
          </div>
          <div className="p1105-field-row">
            <label>聯絡人</label>
            <Input
              value={f105_14}
              onChange={(e) => setF10514(e.target.value)}
              disabled={!isEditing}
              style={{ width: 200 }}
            />
          </div>
          <div className="p1105-field-row">
            <label>電話</label>
            <Input
              value={f105_15}
              onChange={(e) => setF10515(e.target.value)}
              disabled={!isEditing}
              style={{ width: 200 }}
            />
          </div>
          <div className="p1105-field-row">
            <label>庫位等級</label>
            <Select
              value={f105_20 || undefined}
              onChange={(val: string) => setF10520(val)}
              disabled={mode === 'edit' || !isEditing}
              style={{ width: 200 }}
              placeholder="請選擇庫位等級"
              allowClear
            >
              {levelOptions.map((opt) => (
                <Select.Option key={opt.code} value={opt.code}>
                  {opt.code} - {opt.name}
                </Select.Option>
              ))}
            </Select>
          </div>
          <div className="p1105-checkbox-row">
            <Checkbox
              checked={f105_16}
              onChange={(e) => setF10516(e.target.checked)}
              disabled={!isEditing}
            >
              客戶寄庫
            </Checkbox>
            <Checkbox
              checked={f105_17}
              onChange={(e) => setF10517(e.target.checked)}
              disabled={!isEditing}
            >
              庫位控管
            </Checkbox>
            <Checkbox checked={f105_19} disabled>
              控管有明細
            </Checkbox>
            <Checkbox
              checked={f105_18}
              onChange={(e) => setF10518(e.target.checked)}
              disabled={!isEditing}
            >
              慶佑庫位
            </Checkbox>
          </div>

          {/* Audit fields */}
          <div className="p1105-field-row">
            <label>建立人員</label>
            <Input value={f105_91} disabled style={{ width: 120 }} />
          </div>
          <div className="p1105-field-row">
            <label>建立日期</label>
            <Input value={f105_92 ? f105_92.substring(0, 10) : ''} disabled style={{ width: 140 }} />
          </div>
          <div className="p1105-field-row">
            <label>異動人員</label>
            <Input value={f105_93} disabled style={{ width: 120 }} />
          </div>
        </div>

        {/* Detail Table: 控管人員明細 (F1051) */}
        <div className="p1105-detail-section">
          <div className="p1105-detail-header">
            <h4>控管人員明細</h4>
            {isEditing && (
              <Button
                size="small"
                icon={<PlusOutlined />}
                onClick={addDetail}
                disabled={details.length >= 99}
              >
                新增明細
              </Button>
            )}
          </div>
          <Table
            dataSource={details}
            columns={detailColumns}
            rowKey={(_, idx) => String(idx)}
            size="small"
            pagination={false}
            locale={{ emptyText: '無控管人員明細' }}
          />
        </div>

        {/* Action Buttons */}
        <div className="p1105-actions">
          {!isEditing ? (
            <>
              <Button type="primary" icon={<PlusOutlined />} onClick={startAdd}>新增</Button>
              <Button icon={<EditOutlined />} onClick={startEdit} disabled={recordCount === 0}>修改</Button>
              <Button
                danger
                icon={<DeleteOutlined />}
                disabled={recordCount === 0}
                onClick={() => {
                  Modal.confirm({
                    title: '確認刪除',
                    content: `確定要刪除庫位 ${f105_01} 嗎？`,
                    onOk: doDelete,
                  });
                }}
              >
                刪除
              </Button>
            </>
          ) : (
            <>
              <Button type="primary" icon={<SaveOutlined />} onClick={save}>存檔</Button>
              <Button icon={<CloseOutlined />} onClick={cancel}>還原</Button>
            </>
          )}
        </div>
      </div>
    </Spin>
  );
};

export default P1105Form;
